import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';

interface RichMediaAction {
  iconUrl?: string;
  payload?: string;
  text: string;
  type: string;
}

interface RichMediaPayload {
  body?: {
    elementText?: {
      text: string;
      textFormat?: string;
    };
    elementType?: string;
    payload?: {
        actions?: RichMediaAction[];
    };
  };
  payload?: {
    actions?: RichMediaAction[];
  };
}

interface RichMediaResponse {
  payload?: RichMediaPayload;
}

interface ChannelMetadata {
  type: string;
  data: {
    queryResult?: {
      responseMessages?: RichMediaResponse[];
    };
  };
}

interface RichMediaMessageProps {
  metadata: ChannelMetadata;
  onActionClick?: (action: RichMediaAction) => void;
  isMine?: boolean;
  forceDisabled?: boolean;
}

const RichMediaMessage: React.FC<RichMediaMessageProps> = ({
  metadata,
  onActionClick,
  isMine = false,
  forceDisabled
}) => {
  // State to track if any button in this message set has been clicked
const [clickedDisabled, setClickedDisabled] = useState(false);
const isDisabled = clickedDisabled || forceDisabled;
  // Extract response messages from metadata
  const responseMessages = metadata?.data?.queryResult?.responseMessages || [];

  if (responseMessages.length === 0) {
    return null;
  }

  const handleActionClick = (action: RichMediaAction) => {
    // Don't proceed if buttons are already disabled
    if (isDisabled) return;

    console.log('🔘 Rich Media Action Clicked:', action);

    // Disable all buttons in this message set
    setClickedDisabled(true);

    if (onActionClick) {
      onActionClick(action);
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      {responseMessages.map((response, responseIndex) => {
        const payload = response.payload;
        if (!payload) return null;

        return (
          <Box key={responseIndex} sx={{ mb: 2 }}>
            {/* Render body text if available */}
            {payload.body?.elementText?.text && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1,
                  color: isMine ? '#fff' : '#111',
                  fontWeight: payload.body.elementText.textFormat === 'PLAINTEXT' ? 'normal' : 'bold'
                }}
              >
                {payload.body.elementText.text}
              </Typography>
            )}

            {/* Render action buttons */}
            {(() => {
              const actions = payload.payload?.actions || payload.body?.payload?.actions;
              return actions && actions.length > 0 && (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  mt: 1
                }}>
                  {actions.map((action: RichMediaAction, actionIndex: number) => (
                  <Button
                    key={actionIndex}
                    variant="contained"
                    size="small"
                    disabled={isDisabled}
                    onClick={() => handleActionClick(action)}
                    sx={{
                      backgroundColor: isDisabled ? '#cccccc' : '#dc3545', // Gray when disabled, red when active
                      color: isDisabled ? '#666666' : '#fff', // Dark gray text when disabled, white when active
                      borderRadius: '20px',
                      textTransform: 'none',
                      fontSize: '14px',
                      fontWeight: 'normal',
                      minHeight: '36px',
                      maxWidth: '200px',
                      alignSelf: 'flex-start',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      '&:hover': {
                        backgroundColor: isDisabled ? '#cccccc' : '#c82333',
                      },
                      '&:active': {
                        backgroundColor: isDisabled ? '#cccccc' : '#bd2130',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#cccccc',
                        color: '#666666',
                      }
                    }}
                  >
                    {action.text}
                  </Button>
                  ))}
                </Box>
              );
            })()}
          </Box>
        );
      })}
    </Box>
  );
};

export default RichMediaMessage;
